import React, { useEffect, useState } from "react";
import { useTheme } from "../context/ThemeContext";
import { useLanguage } from "../context/LanguageContext";
import logoInfo, { LogoInfo } from "./tech_logos";

interface LogoModule {
  default: string;
}

const IntegrationSection: React.FC = () => {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const [logos, setLogos] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadLogos = async () => {
      try {
        const logoModules = import.meta.glob<LogoModule>("/src/assets/tech_logos/*.{png,jpg,jpeg,svg,webp}");
        if (Object.keys(logoModules).length === 0) {
          throw new Error("No logos found in /src/assets/tech_logos/. Please add images to this folder.");
        }

        const logoPaths = await Promise.all(
          Object.keys(logoModules).map(async (path) => {
            const module = await logoModules[path]();
            return module.default;
          })
        );

        setLogos(logoPaths);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadLogos();
  }, []);

  if (loading) {
    return (
      <section className="flex items-center justify-center py-16">
        <p className="text-lg text-gray-500">Loading logos...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex items-center justify-center py-16">
        <p className="text-lg text-red-500">Error: {error}</p>
      </section>
    );
  }

  const isDark = theme === "dark";

  return (
    <section
      id="integration"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12 sm:mb-16 md:mb-20">
          <h2 className={`
            text-3xl sm:text-4xl md:text-5xl lg:text-6xl 
            font-bold 
            ${isDark 
              ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
              : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
            }
            bg-clip-text 
            text-transparent
            mb-6
            animate-gradient-x
          `}
          style={{ lineHeight: '1.2', paddingBottom: '10px' }}>
            {language === "en" ? "Integrations" : "Intégrations"}
          </h2>
          <p className={`text-lg sm:text-xl md:text-2xl font-medium mb-8 ${
            isDark ? "text-gray-300" : "text-gray-700"
          }`}>
            {language === "en" 
              ? "Connect with your favorite tools and platforms" 
              : "Connectez-vous à vos outils et plateformes préférés"}
          </p>
          <div className={`w-16 sm:w-20 md:w-24 h-1 mx-auto rounded-full mt-4 sm:mt-6 ${
            isDark 
              ? "bg-gradient-to-r from-green-500/50 to-green-300/50" 
              : "bg-gradient-to-r from-green-900/50 to-green-700/50"
          }`} />
        </div>

        {/* Logo Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 sm:gap-6 lg:gap-8">
          {logos.map((logo, index) => {
            const logoData = logoInfo.find((info: LogoInfo) => info.image === logo);
            const description = language === "en"
              ? logoData?.descriptionEn || "No description available."
              : logoData?.descriptionFr || "Aucune description disponible.";

            return (
              <div
                key={index}
                className={`
                  group
                  relative
                  p-4
                  rounded-xl
                  border-2
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  overflow-hidden
                  ${isDark 
                    ? "bg-gray-900/90 border-green-500/30 hover:border-green-400" 
                    : "bg-white/80 border-green-900/30 hover:border-green-800"
                  }
                `}
              >
                {/* Radial Gradient Background */}
                <div className={`
                  absolute 
                  inset-0 
                  opacity-10
                  group-hover:opacity-15
                  transition-opacity
                  duration-300
                  ${isDark
                    ? "bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/10 via-white/5 to-transparent"
                    : "bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/40 via-white/20 to-transparent"
                  }
                `} />

                <div className="aspect-square flex items-center justify-center relative z-10">
                  <img
                    src={logo}
                    alt={logoData?.name || `Integration ${index + 1}`}
                    className={`
                      w-full h-full object-contain p-2 
                      transition-all duration-300 
                      group-hover:scale-110
                      ${isDark ? "brightness-125 contrast-125" : ""}
                    `}
                  />
                </div>
                
                {/* Tooltip */}
                <div className={`
                  absolute 
                  left-1/2 
                  -translate-x-1/2 
                  bottom-full 
                  mb-2
                  w-48
                  p-2
                  rounded-lg
                  text-sm
                  opacity-0
                  group-hover:opacity-100
                  transition-opacity
                  duration-300
                  z-10
                  ${isDark 
                    ? "bg-black text-white border border-green-500/30" 
                    : "bg-white text-black border border-green-900/30"
                  }
                `}>
                  <p className="font-bold mb-1">{logoData?.name || "Integration"}</p>
                  <p className="text-xs">{description}</p>
                </div>
              </div>
            );
          })}
          
          {/* "And Many More" Card */}
          <div className={`
            aspect-square
            rounded-xl
            border-2
            flex
            items-center
            justify-center
            text-center
            p-4
            relative
            overflow-hidden
            transition-all
            duration-300
            hover:-translate-y-1
            ${isDark 
              ? "bg-gray-900/90 border-green-500/30 hover:border-green-400" 
              : "bg-white/80 border-green-900/30 hover:border-green-800"
            }
          `}>
            {/* Radial Gradient Background */}
            <div className={`
              absolute 
              inset-0 
              opacity-10
              group-hover:opacity-15
              transition-opacity
              duration-300
              ${isDark
                ? "bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/10 via-white/5 to-transparent"
                : "bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/40 via-white/20 to-transparent"
              }
            `} />

            <p className={`
              relative
              z-10
              font-medium
              text-base sm:text-lg
              ${isDark ? "text-gray-300" : "text-gray-700"}
            `}>
              {language === "en" ? "And Many More" : "Et Bien Plus"}
            </p>
          </div>
        </div>
      </div>

      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
        <div className={`absolute bottom-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 rounded-full blur-3xl ${
          isDark ? "bg-green-500/5" : "bg-green-900/5"
        }`} />
      </div>
    </section>
  );
};

export default IntegrationSection;
